import { DEFAULT_GRID_SIZE, GRID_SIZES } from '../components/layouts/pageLayoutModel';
import { getSessionUserId } from './session';

/**
 * How the person likes the page-layout editor's canvas (KPR-345): snap to the
 * grid, the grid's size, and the rulers; and (KPR-348) whether alignment guides
 * pull a carried block onto another's edge. A view preference, like the items
 * page's grid/table choice: it changes nothing a layout stores, so it lives in
 * this browser (`localStorage`, never authoritative) and not on the server,
 * where it would be a contract change for a convenience.
 *
 * Kept PER ACCOUNT (`keepr.pageEditor:<userId>`, the idiom of utils/recents.js)
 * so a second person on the same browser does not inherit the first one's
 * canvas. Signed out there is nothing to key by, so nothing is read or kept.
 *
 * `grid` is a SIZE name (`fine` / `medium` / `coarse`), not a number: the
 * choice survives a change of paper from inches to millimetres (components/
 * layouts/pageLayoutModel.js gridStep).
 */

export const PAGE_EDITOR_VIEW_DEFAULTS = Object.freeze({ snap: true, grid: DEFAULT_GRID_SIZE, rulers: true, guides: true });

const KEY = 'keepr.pageEditor';
const keyFor = userId => (userId ? `${KEY}:${userId}` : null);

/** Whatever was stored, repaired field by field: a bad value costs only itself. */
export const normalizePageEditorView = (stored) => {
	const source = stored && typeof stored === 'object' ? stored : {};
	return {
		snap: typeof source.snap === 'boolean' ? source.snap : PAGE_EDITOR_VIEW_DEFAULTS.snap,
		grid: GRID_SIZES.includes(source.grid) ? source.grid : PAGE_EDITOR_VIEW_DEFAULTS.grid,
		rulers: typeof source.rulers === 'boolean' ? source.rulers : PAGE_EDITOR_VIEW_DEFAULTS.rulers,
		guides: typeof source.guides === 'boolean' ? source.guides : PAGE_EDITOR_VIEW_DEFAULTS.guides,
	};
};

export const readPageEditorView = (userId = getSessionUserId()) => {
	const key = keyFor(userId);
	if (!key) return { ...PAGE_EDITOR_VIEW_DEFAULTS };
	try {
		return normalizePageEditorView(JSON.parse(window.localStorage.getItem(key) || 'null'));
	} catch {
		return { ...PAGE_EDITOR_VIEW_DEFAULTS };
	}
};

export const writePageEditorView = (view, userId = getSessionUserId()) => {
	const key = keyFor(userId);
	if (!key) return;
	try {
		window.localStorage.setItem(key, JSON.stringify(normalizePageEditorView(view)));
	} catch { /* a per-viewer convenience: the canvas still works */ }
};
